import React, { useCallback } from 'react';
import { SharedLocation, SharingMethod } from '@bringg/types';
import Timezone from '../timezone/timezone';
import DateDisplay from '../date-display/date-display';
import { WindowCommunicationService } from 'features/waypoint-card/waypoint-card.consts';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

interface Props {
	sharedLocations: SharedLocation[];
	timeZone: string;
	dateFormat: string;
	translations: Translations;
	useTeamTimezone?: boolean;
	WindowCommunicationService: WindowCommunicationService;
}

export interface Translations {
	sharedPhone: string;
	sharedEmail: string;
	sharedApp: string;
	sharedWebhook: string;
	firstSeen: string;
	viewed: string;
	times: string;
	link: string;
}

const TaskTrackingLinks: React.FC<Props> = props => {
	const links = props.sharedLocations.map(sharedLocation => (
		<TaskTrackingLink key={sharedLocation.id} sharedLocation={sharedLocation} {...props} />
	));

	return <div className="task-tracking-links-container">{links}</div>;
};

type TaskTrackingLinkProps = Omit<Props, 'sharedLocations'> & {
	sharedLocation: SharedLocation;
};

const TaskTrackingLink: React.FC<TaskTrackingLinkProps> = ({
	sharedLocation,
	timeZone,
	dateFormat,
	translations,
	useTeamTimezone,
	WindowCommunicationService
}) => {
	const getSharingMethodTitle = useCallback((): string => {
		switch (sharedLocation.sharing_method) {
			case SharingMethod.Phone: {
				return translations.sharedPhone;
			}

			case SharingMethod.Email: {
				return translations.sharedEmail;
			}

			case SharingMethod.Websocket: {
				return translations.sharedApp;
			}

			case SharingMethod.Webhook: {
				return translations.sharedWebhook;
			}
		}
	}, [sharedLocation, translations]);

	const openCX = (sharedLocation: SharedLocation) => {
		const openedWindow = window.open(sharedLocation.url, '_blank');
		WindowCommunicationService?.sendCredentials(openedWindow);
	};

	return (
		<div className="task-tracking-link-container" data-test-id={sharedLocation.id}>
			<div className="task-tracking-link-title">
				<BringgIcon iconName={BringgFontIcons.Location} className="link-icon" /> {getSharingMethodTitle()}
			</div>

			<DateDisplay date={sharedLocation.shared_on} format={dateFormat} timezone={useTeamTimezone && timeZone} />
			<Timezone timezone={timeZone} />

			<div className="task-tracking-link-title">{translations.firstSeen}</div>

			<DateDisplay
				date={sharedLocation.first_seen_on}
				format={dateFormat}
				timezone={useTeamTimezone && timeZone}
			/>
			<Timezone timezone={timeZone} />
			<span className="task-tracking-link-view-counter">
				{translations.viewed} {sharedLocation.view_counter} {translations.times}
			</span>
			<div className="task-tracking-link-url" onClick={() => openCX(sharedLocation)}>
				<a href={sharedLocation.url} onClick={e => e.preventDefault()}>
					{translations.link}
				</a>
			</div>
		</div>
	);
};

export default TaskTrackingLinks;
